import React from "react";
import PropTypes from "prop-types";

/**
 * SkeletonLoader component for showing loading placeholders
 *
 * Provides various skeleton shapes and animations for better user experience
 * during loading states. Supports different variants for different content types.
 *
 * Requirements: 8.4, 8.5 - Loading states and skeleton loaders for better UX
 */
const SkeletonLoader = ({
  variant = "text",
  width = "100%",
  height = "1rem",
  className = "",
  count = 1,
  animate = true,
  ...rest
}) => {
  // Base skeleton classes with animation
  const baseClasses = `
    bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 
    rounded
    ${animate ? "animate-pulse" : ""}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  // Variant-specific styles
  const getVariantStyles = () => {
    switch (variant) {
      case "text":
        return {
          width: width,
          height: height,
        };
      case "circle":
        return {
          width: width,
          height: width, // Make it square, then border-radius will make it circular
          borderRadius: "50%",
        };
      case "avatar":
        return {
          width: width || "2.5rem",
          height: width || "2.5rem",
          borderRadius: "50%",
        };
      case "button":
        return {
          width: width || "5rem",
          height: height || "2.25rem",
          borderRadius: "0.375rem", // rounded-md
        };
      case "card":
        return {
          width: width,
          height: height || "8rem",
          borderRadius: "0.5rem", // rounded-lg
        };
      case "line":
        return {
          width: width,
          height: height || "0.75rem",
        };
      default:
        return {
          width: width,
          height: height,
        };
    }
  };

  const variantStyles = getVariantStyles();

  // Render multiple skeletons if count > 1
  if (count > 1) {
    return (
      <div className="space-y-2" {...rest}>
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className={baseClasses} style={variantStyles} />
        ))}
      </div>
    );
  }

  return <div className={baseClasses} style={variantStyles} {...rest} />;
};

/**
 * UserCardSkeleton - Specialized skeleton for user cards
 */
export const UserCardSkeleton = ({
  compact = false,
  showFollowButton = true,
}) => {
  return (
    <div className={`flex items-center gap-3 p-3 ${compact ? "py-2" : ""}`}>
      {/* Avatar */}
      <SkeletonLoader variant="avatar" width={compact ? "2rem" : "2.5rem"} />

      {/* User info */}
      <div className="flex-1 space-y-1">
        <SkeletonLoader
          variant="text"
          width="60%"
          height={compact ? "0.875rem" : "1rem"}
        />
        <SkeletonLoader variant="text" width="40%" height="0.75rem" />
      </div>

      {/* Follow button */}
      {showFollowButton && (
        <SkeletonLoader
          variant="button"
          width="4rem"
          height={compact ? "1.75rem" : "2rem"}
        />
      )}
    </div>
  );
};

/**
 * FollowersListSkeleton - Specialized skeleton for followers/following lists
 */
export const FollowersListSkeleton = ({ count = 5, compact = false }) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }, (_, index) => (
        <UserCardSkeleton
          key={index}
          compact={compact}
          showFollowButton={true}
        />
      ))}
    </div>
  );
};

/**
 * ProfileStatsSkeleton - Specialized skeleton for profile statistics
 */
export const ProfileStatsSkeleton = () => {
  return (
    <div className="grid grid-cols-2 gap-4">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex items-center gap-3">
          <SkeletonLoader variant="circle" width="2rem" />
          <div className="flex-1 space-y-1">
            <SkeletonLoader variant="text" width="50%" height="0.75rem" />
            <SkeletonLoader variant="text" width="70%" height="1rem" />
          </div>
        </div>
      ))}
    </div>
  );
};

SkeletonLoader.propTypes = {
  variant: PropTypes.oneOf([
    "text",
    "circle",
    "avatar",
    "button",
    "card",
    "line",
  ]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  count: PropTypes.number,
  animate: PropTypes.bool,
};

UserCardSkeleton.propTypes = {
  compact: PropTypes.bool,
  showFollowButton: PropTypes.bool,
};

FollowersListSkeleton.propTypes = {
  count: PropTypes.number,
  compact: PropTypes.bool,
};

export default SkeletonLoader;
